import { Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, withLatestFrom } from 'rxjs';
import * as AuthSelectors from '../../auth/store/selectors/auth.selector';
import * as GeneralSelectors from '../../+state/general.selectors';
import * as AuthActions from '../../auth/store/actions/auth.actions';
import * as GeneralActions from '../../+state/general.actions';
import * as UserPortfolioActions from '../../+state/user-portfolio/user-portfolio.actions';
import { AppRoutesEnum } from '../../enums/app-routes.enum';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map } from 'rxjs/operators';
import { PermissionsService } from '../../services/permissions.service';
import { routePermissions } from '../../permissions/route-permissions';
import { invitesSelector } from '../../+state/user-portfolio/user-portfolio.selectors';
import { DataServices } from '../../enums/data-services.enum';
import { AccessServiceActions } from '../../enums/service-actions/access-service-actions.enum';
import { AccessType } from '../../enums/access-type.enum';
import { TreeViewItem } from '../../../../../../libs/frogo-kit/src/lib/components/tree-view/models/tree-view-item.interface';
import { ProfileInfo, SelectOption } from '../../../../../../libs/frogo-kit/src/lib/components/navbar/navbar.component';
import { FeatureName, featurePermissions } from '../../permissions/feature-permissions';
import { ModalService } from '@aft/frogo-kit/services/modal.service';
import { ChangePasswordComponent } from '../../modals/change-password/change-password.component';
import { LocalStorageKeys } from '../../enums/local-storage-keys.enum';
import { ProfileComponent } from '../../modals/profile/profile.component';
import { OtpSetupComponent } from '../../modals/otp-setup/otp-setup.component';

@UntilDestroy()
@Component({
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    standalone: false,
})
export class LayoutComponent {
  public userName$: Observable<ProfileInfo> = this.store.select(AuthSelectors.userDataSelector)
    .pipe(
      map(({ userData }) => userData),
      filter(Boolean),
      map((userData) => ({
        name: userData.name,
        email: userData.email,
      })),
    );
  public selectedLang$: Observable<string> = this.store.select(GeneralSelectors.langSelector);
  public invites$ = this.store.select(invitesSelector);
  public langOptions = [
    {
      lang: 'en',
      icon: '../assets/img/flags/en.svg',
    },
    {
      lang: 'ua',
      icon: '../assets/img/flags/ua.svg',
    },
  ];

  menuItems$: Observable<TreeViewItem[]>;
  secondaryMenuItems$: Observable<TreeViewItem[]>;

  public canAccessOverall$: Observable<boolean> = this.permissionsService
    .hasPermission$(routePermissions[AppRoutesEnum.InvestigationsOverall]);
  public canAccessGraphs$: Observable<boolean> = this.permissionsService
    .hasPermission$(routePermissions[AppRoutesEnum.InvestigationsEntity]);
  public canAccessPoliciesList$: Observable<boolean> = this.permissionsService
    .hasPermission$(routePermissions[AppRoutesEnum.PolicySettingsLists]);
  public canAccessPolicies$: Observable<boolean> = this.permissionsService
    .hasPermission$(routePermissions[AppRoutesEnum.PolicySettingsPolicies]);
  public canAccessPoliciesAlerts$: Observable<boolean> = this.permissionsService
    .hasPermission$(routePermissions[AppRoutesEnum.PolicySettingsAlerts]);
  public canAccessOrganizations$: Observable<boolean> = this.permissionsService
    .hasPermission$(routePermissions[AppRoutesEnum.Organization]);
  public canAccessUserManagementUsers$: Observable<boolean> = this.permissionsService
    .hasPermission$(routePermissions[AppRoutesEnum.UsersManagementUsers]);
  public canAccessUserManagementRoles$: Observable<boolean> = this.permissionsService
    .hasPermission$(routePermissions[AppRoutesEnum.UsersManagementRoles]);
  public canSwitchOrganizations$: Observable<boolean> = this.permissionsService
    .hasPermission$(featurePermissions.get(FeatureName.OrganizationSwitch));
  public canAcceptInvites$: Observable<boolean> = this.permissionsService
    .hasPermission$(featurePermissions.get(FeatureName.UMAcceptInvites));

  public requiredUser2FAInit$
    = this.store.select(AuthSelectors.requiredUser2FAInit);
  public canReadOtp$: Observable<boolean> = this.permissionsService
    .hasPermission$(featurePermissions.get(FeatureName.OtpInitData));

  public i18nNavMenu = {
    'title': this._translate.instant('navMenu.notifications.title'),
    'accept': this._translate.instant('navMenu.notifications.accept'),
    'reject': this._translate.instant('navMenu.notifications.reject'),
  }

  sharedOrganizations$: Observable<SelectOption[]> = this.store.select(AuthSelectors.sharedOrgsSelector)
    .pipe(
      withLatestFrom(this.canSwitchOrganizations$),
      map(([data, canSwitchOrganization]) => {
        return canSwitchOrganization
          ? data.shared.map(organization => ({ id: organization.id, label: organization.name }))
          : [];
      }),
    );
  selectedOrganization$: Observable<string> = this.store.select(AuthSelectors.sharedOrgsSelector)
    .pipe(
      map(data => data.selectedOrg),
    );

  constructor(
    private store: Store,
    private translate: TranslateService,
    private activatedRoute: ActivatedRoute,
    private permissionsService: PermissionsService,
    private readonly _modal: ModalService,
    private readonly _translate: TranslateService,
  ) {
    const { permissions } = this.activatedRoute.snapshot.data;

    this.store.dispatch(AuthActions.setPermissions({ permissions }));

    if (this.permissionsService.hasPermission([`${DataServices.Access}.${AccessServiceActions.Invites}.${AccessType.Read}`])) {
      this.store.dispatch(UserPortfolioActions.getUserInvations());
    }

    this.menuItems$ = this.getMenuItems();
    this.secondaryMenuItems$ = this.getSecondaryMenuItems();

    if ( localStorage.getItem(LocalStorageKeys.RequestPasswordChange) ) {
      this.changePassword();
    }
  }

  getMenuItems(): Observable<TreeViewItem[]> {
    return combineLatest(
      this.canAccessOverall$,
      this.canAccessGraphs$,
      this.canAccessPolicies$,
      this.canAccessPoliciesList$,
      this.canAccessPoliciesAlerts$,
    )
      .pipe(
        untilDestroyed(this),
        map((
          [
            canAccessOverall,
            canAccessGraphs,
            canAccessPolicies,
            canAccessPoliciesList,
            canAccessPoliciesAlerts,
          ],
        ) => (
          [
            ...this.investigationsItemsGroup(canAccessOverall, canAccessGraphs),
            ...this.policiesItemsGroup(canAccessPolicies, canAccessPoliciesList, canAccessPoliciesAlerts),
          ]
        )),
      );
  }

  getSecondaryMenuItems(): Observable<TreeViewItem[]> {
    return combineLatest(
      this.canAccessUserManagementUsers$,
      this.canAccessUserManagementRoles$,
      this.canAccessOrganizations$,
    )
      .pipe(
        untilDestroyed(this),
        map((
          [
            canAccessUserManagementUsers,
            canAccessUserManagementRoles,
            canAccessOrganizations,
          ]) => {
          return [
            ...this.settingsGroupItems(canAccessUserManagementUsers, canAccessUserManagementRoles, canAccessOrganizations),
          ];
        }),
      );
  }

  public acceptInvite(inviteId: string) {
    this.store.dispatch(UserPortfolioActions.acceptInvite({ inviteId }))
  }

  public rejectInvite(inviteId: string) {
    this.store.dispatch(UserPortfolioActions.rejectInvite({ inviteId }))
  }

  public changePassword(): void {
    this._modal.show(ChangePasswordComponent, {
      title: this.translate.instant('layoutNavigation.changePassword'),
    });
  }

  public openSetup2FAModal() {
    this._modal.show(OtpSetupComponent);
  }

  public openProfile(): void {
    this._modal.show(ProfileComponent, { title: '' });
  }

  protected switchOrganization(id: string): void {
    this.store.dispatch(AuthActions.switchOrganization({ id }));
  }

  protected switchLanguage(lang: string, selectedLang: string): void {
    if (lang !== selectedLang) {
      this.store.dispatch(GeneralActions.setLang({ lang }));
    }
  }

  protected logout(): void {
    this.store.dispatch(AuthActions.logout());
  }

  private investigationsItemsGroup(
    canAccessOverall: boolean,
    canAccessGraphs: boolean,
  ): TreeViewItem[] {
    const hasSomeItem = canAccessOverall || canAccessOverall;

    return [
      ...(
        hasSomeItem
          ? [{
            label: this.translate.instant('navMenu.investigations.title'),
            icon: 'file-search',
            items: [
              ...this.investigationOverallItem(canAccessOverall),
              ...this.investigationEntityItem(canAccessGraphs),
            ],
          }]
          : []
      ),
    ];
  }

  private investigationOverallItem(hasPermission: boolean): TreeViewItem[] {
    return hasPermission
      ? [{
        label: this.translate.instant('navMenu.investigations.overall'),
        link: `/${AppRoutesEnum.Investigations}/${AppRoutesEnum.InvestigationsOverall}`,
      }]
      : [];
  }

  private investigationEntityItem(hasPermission: boolean): TreeViewItem[] {
    return hasPermission
      ? [{
        label: this.translate.instant('navMenu.investigations.entity'),
        link: `/${AppRoutesEnum.Investigations}/${AppRoutesEnum.InvestigationsEntity}`,
      }]
      : [];
  }

  private policiesItemsGroup(
    canAccessPolicies: boolean,
    canAccessPolicyLists: boolean,
    canAccessPolicyAlerts: boolean,
  ): TreeViewItem[] {
    const hasSomeItem = canAccessPolicies
    || canAccessPolicyLists
    || canAccessPolicyAlerts;

    return [
      ...(
        hasSomeItem
          ? [{
            label: this.translate.instant('navMenu.policy.title'),
            icon: 'shield-zap',
            items: [
              ...this.policyAlertsItem(canAccessPolicyAlerts),
              ...this.policyListItem(canAccessPolicyLists),
              ...this.policyPoliciesItem(canAccessPolicies),
            ],
          }]
          : []
      ),
    ];
  }

  private policyAlertsItem(hasPermission: boolean): TreeViewItem[] {
    return hasPermission
      ? [{
        label: this.translate.instant('navMenu.policy.alerts'),
        link: `/${AppRoutesEnum.PolicySettings}/${AppRoutesEnum.PolicySettingsAlerts}`,
      }]
      : [];
  }

  private policyListItem(hasPermission: boolean): TreeViewItem[] {
    return hasPermission
      ? [{
        label: this.translate.instant('navMenu.policy.lists'),
        link: `/${AppRoutesEnum.PolicySettings}/${AppRoutesEnum.PolicySettingsLists}`,
      }]
      : [];
  }

  private policyPoliciesItem(hasPermission: boolean): TreeViewItem[] {
    return hasPermission
      ? [{
        label: this.translate.instant('navMenu.policy.policies'),
        link: `/${AppRoutesEnum.PolicySettings}/${AppRoutesEnum.PolicySettingsPolicies}`,
      }]
      : [];
  }

  private organizationItem(hasPermission: boolean): TreeViewItem[] {
    return hasPermission
      ? [{
        label: this.translate.instant('navMenu.organization.title'),
        link: `/${AppRoutesEnum.Organization}`,
      }]
      : [];
  }

  private userManagementItem(hasPermissionUsers: boolean, hasPermissionRoles: boolean): TreeViewItem[] {
    return (hasPermissionUsers || hasPermissionRoles)
      ? [{
        label: this.translate.instant('navMenu.usersManagement.title'),
        link: `/${AppRoutesEnum.UsersManagement}`,
      }]
      : [];
  }

  private settingsGroupItems(
    canAccessUserManagementUsers: boolean,
    canAccessUserManagementRoles: boolean,
    canAccessOrganizations: boolean,
  ): TreeViewItem[] {
    const hasSomeItem = canAccessUserManagementUsers || canAccessUserManagementRoles || canAccessOrganizations;

    return [
      ...(
        hasSomeItem
          ? [{
            label: this.translate.instant('navMenu.settings.title'),
            icon: 'settings',
            items: [
              ...this.userManagementItem(canAccessUserManagementUsers, canAccessUserManagementRoles),
              ...this.organizationItem(canAccessOrganizations),
            ],
          }]
          : []
      ),
    ];
  }
}
